import { Middleware } from "@reduxjs/toolkit";
import {
  pushHistorySnapshot,
  clearFieldHistory,
  setClipboardField,
  FieldHistorySnapshot,
} from "redux/appSlice";

const TEXT_ONLY_KEYS = new Set([
  "label",
  "placeholder",
  "toolTip",
  "validationMessage",
  "maxLength",
  "tabLabel",
]);

const getDocId = (file: any): string =>
  (file && (file.unsignedDoc || file.unsignedPdf)) || "";

const cloneFields = (fields: any[]): any[] =>
  JSON.parse(JSON.stringify(fields || []));

const payloadHasOnlyTextKeys = (payload: any): boolean => {
  if (!payload || typeof payload !== "object") return false;
  const keys = Object.keys(payload).filter((k) => k !== "id");
  if (keys.length === 0) return false;
  return keys.every((k) => TEXT_ONLY_KEYS.has(k));
};

const payloadChangesOnlyTextKeys = (
  payload: any,
  fieldsBefore: any[],
): boolean => {
  if (!payload || typeof payload !== "object") return false;
  const keys = Object.keys(payload).filter((k) => k !== "id");
  if (keys.length === 0) return true;
  const before = fieldsBefore.find((f) => f && f.id === payload.id);
  if (!before) return false;
  const changedKeys = keys.filter((k) => {
    try {
      return JSON.stringify(before[k]) !== JSON.stringify(payload[k]);
    } catch {
      return before[k] !== payload[k];
    }
  });
  if (changedKeys.length === 0) return true;
  return changedKeys.every((k) => TEXT_ONLY_KEYS.has(k));
};

const fieldsEqual = (a: any[], b: any[]): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
};

const recordSnapshot = (
  store: any,
  docId: string,
  fields: any[],
  selectedFieldId: number | null,
) => {
  if (!docId) return;
  const snapshot: FieldHistorySnapshot = {
    fields: cloneFields(fields),
    selectedFieldId,
  };
  store.dispatch(pushHistorySnapshot({ docId, snapshot }));
};

let lastSignRequestId: string | null = null;

export const historyMiddleware: Middleware =
  (store) => (next) => (action: any) => {
    if (!action || typeof action !== "object" || !action.type) {
      return next(action);
    }

    if (
      action.type === "app/pushHistorySnapshot" ||
      action.type === "app/undoFieldChange" ||
      action.type === "app/redoFieldChange" ||
      action.type === "app/clearFieldHistory"
    ) {
      return next(action);
    }

    if (action.meta?.skipHistory === true) {
      const result = next(action);
      syncSignRequestId(store);
      return result;
    }

    const stateBefore = store.getState();
    const appBefore = stateBefore.app;
    const selectedFileBefore = appBefore?.selectedFile;
    const selectedFieldBefore = appBefore?.selectedField;
    const currentDocId = getDocId(selectedFileBefore);
    const fieldsBefore = selectedFileBefore?.fields || [];

    const payload = action.payload;

    // Each user action (add/move/resize/delete/duplicate/property change) is
    // dispatched as a pair: leading reducer mutates `selectedFile`, trailing
    // reducer mirrors the change to `signRequest.files`. We only intercept the
    // leading dispatch so the pair becomes one snapshot. The trailing dispatch
    // (`updateFileInSignRequest` / `updateFieldInSignRequest`) is intentionally
    // not snapshotted here.
    let shouldRecord = false;
    switch (action.type) {
      case "app/setReduxSelectedFile": {
        if (!payload) break;
        const payloadDocId = getDocId(payload);
        if (!payloadDocId || payloadDocId !== currentDocId) break;
        if (!Array.isArray(payload.fields)) break;
        if (fieldsBefore === payload.fields) break;
        shouldRecord = true;
        break;
      }
      case "app/deleteFieldOfSelectedFile": {
        if (!currentDocId) break;
        shouldRecord = true;
        break;
      }
      case "app/updateFieldOfSelectedFile": {
        if (!currentDocId) break;
        if (payloadHasOnlyTextKeys(payload)) break;
        if (payloadChangesOnlyTextKeys(payload, fieldsBefore)) break;
        shouldRecord = true;
        break;
      }
      case "app/setRadioGroupValue": {
        if (!currentDocId) break;
        shouldRecord = true;
        break;
      }
      default:
        break;
    }

    const fieldsBeforeSnapshot = shouldRecord
      ? cloneFields(fieldsBefore)
      : null;
    const selectedFieldIdBefore = selectedFieldBefore?.id || null;

    const result = next(action);

    if (shouldRecord && fieldsBeforeSnapshot && currentDocId) {
      const fieldsAfter = store.getState().app?.selectedFile?.fields || [];
      if (!fieldsEqual(fieldsBeforeSnapshot, fieldsAfter)) {
        recordSnapshot(
          store,
          currentDocId,
          fieldsBeforeSnapshot,
          selectedFieldIdBefore,
        );
      }
    }

    syncSignRequestId(store);
    return result;
  };

const syncSignRequestId = (store: any) => {
  const id = store.getState()?.app?.signRequest?.id ?? null;
  if (lastSignRequestId === null) {
    lastSignRequestId = id;
    return;
  }
  if (id !== lastSignRequestId) {
    lastSignRequestId = id;
    store.dispatch(clearFieldHistory(undefined));
    store.dispatch(setClipboardField(null));
  }
};
